import React from 'react';
import type {Dispatch, RefObject, SetStateAction} from 'react';

import {keyframes} from '@emotion/react';
import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import * as motion from 'motion/react-m';

import {Button} from '../../action/Button/Button';
import {ModalEnterExitAnimation} from '../../animations/modals';
import {Close} from '../../icons/Close';

import {useModalAnimation} from './useModalAnimation';

import type {WithChildren} from '../../../utils/childTypes';
import type {Variant, Variants} from 'motion';

export interface ModalEnterExitAnimations extends Variants {
    enter: Variant;
    exit: Variant;
}

/**
 * Interface for the props used by the `Modal` component.
 * This component provides a customizable modal dialog, which can be resized and tested using a unique `testId`.
 */
export interface ModalProps {
    /**
     * Defines the animation variants for the modal's enter and exit transitions.
     * This property is used to specify the animation behavior when the modal is opened or closed.
     */
    modalEnterExitAnimation?: ModalEnterExitAnimations;
    /**
     * Defines the size of the modal. It can either be 'default' or 'small'.
     * The 'default' size is used by default unless otherwise specified.
     * This prop determines the visual size of the modal dialog on the screen.
     *
     * @default 'default'
     */
    size?: 'default' | 'small';
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'Modal'
     */
    testId?: string;
}

/**
 * The `modalFactory` function is a factory function that generates a `Modal` component.
 * This modal can be customized with size and testId props, handles opening and closing actions,
 * and provides a close button to dismiss the modal. It uses animation for enter and exit transitions.
 *
 * @param state The reference to the state that controls the visibility of the modal.
 * @returns A `Modal` component that renders a modal window with customizable size and content.
 *
 * @example
 * ```tsx
 * const Modal = modalFactory(useState(false)[1]);
 * <Modal size="small" testId="myModal">
 *   <p>Modal content</p>
 * </Modal>
 * ```
 */
const modalFactory = (state: RefObject<Dispatch<SetStateAction<boolean>> | null>) => {
    /**
     * The `Modal` component renders a modal window that can contain various child elements and handles opening and closing actions.
     * It uses animations for entering and exiting the modal, and provides a close button to dismiss the modal.
     *
     * @param props                         The component props.
     * @param props.children                The content to be displayed inside the modal.
     * @param props.modalEnterExitAnimation The animation variants for the modal's enter and exit transitions, defining how the modal should animate when it opens and closes.
     * @param props.size                    The size of the modal window. Can be `default` or other preset sizes.
     * @param props.testId                  The `testId` property represents a unique identifier for testing purposes.
     * @returns A JSX element representing the modal window with content and close functionality.
     *
     * @example
     * ```tsx
     * <Modal size="large" testId="custom-modal">
     *   <p>Modal content goes here.</p>
     * </Modal>
     * ```
     */
    const Modal = (
        {
            children,
            modalEnterExitAnimation = ModalEnterExitAnimation,
            size = 'default',
            testId = 'Modal'
        }: WithChildren<ModalProps>
    ) => {
        const {controls, modal, setOpen, showContent} = useModalAnimation(state);

        return (
            <Wrapper
                ref={modal}
                $size={size}
                animate={controls}
                data-cy={testId}
                variants={modalEnterExitAnimation}
            >
                <CloseWrapper onClick={() => setOpen(false)}>
                    <CloseButton
                        as="button"
                        icon={Close}
                        variant="secondary"
                        onClick={() => setOpen(false)}
                    />
                </CloseWrapper>
                {showContent && children}
            </Wrapper>
        );
    };

    Modal.displayName = 'Modal';
    Modal.defaultProps = {
        size: 'default',
        testId: undefined
    };

    return Modal;
};

export {modalFactory};

const fadeIn = keyframes`
    0% {
        opacity: 0;
    }
    100% {
        opacity: 1;
    }
`;

interface WrapperProps {
    $size: 'default' | 'small';
}

const Wrapper = styled(motion.dialog)<WrapperProps>`
    align-content: stretch;
    background: ${({theme}) => theme.colors.neutral0};
    border: none;
    border-radius: 8px;
    height: max-content;
    left: 50%;
    max-height: 86%;
    max-width: ${({$size}) => ($size === 'small' ? '66rem' : '106.4rem')};
    min-height: 20rem;
    opacity: 0;
    outline: none;
    overflow: hidden;
    padding: ${spacing(20)};
    top: 50%;
    transform: translateX(-50%) translateY(calc(-50% - 80px));
    width: 95%;

    &[open] {
        display: grid;
    }

    &::backdrop {
        animation: ${fadeIn} 0.3s ease-in-out backwards;
        animation-direction: reverse;
        background-color: rgba(0, 0, 0, 0.7);
    }

    &[open]::backdrop {
        animation: ${fadeIn} 0.3s ease-in-out forwards;
    }
`;

const CloseWrapper = styled.div`
    position: absolute;
    right: ${spacing(5)};
    top: ${spacing(5)};
    z-index: 10;
`;

const CloseButton = styled(Button)`
    height: fit-content;
    padding: ${spacing(1)};
    width: fit-content;
`;